import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  BRAIN_DUMP_LIMIT_NOTE,
  BRAIN_DUMP_MAX_MS,
  BRAIN_DUMP_QUESTION_TEXT,
  BRAIN_DUMP_SILENCE_MS,
  BRAIN_DUMP_SILENCE_NOTE,
} from '@cerebro-diem/core';
import { CheckCircle2, Loader2, Mic, Square } from 'lucide-react';
import { cleanupRecordingReceipts, retireRecordingReceipt, dropQuestion, prepareQuestion, retryQuestionsStorage, rememberDump, completeDump, pendingDumps, processOwnerDump, flushClearThoughts, saveThoughts, splitBrainDump, useQuestions, useQuestionState } from '../../services/brainDump';
import { useAuthStore } from '../../stores/authStore';
import DictationTextarea from '../../components/DictationTextarea';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import { useVoiceRecorder, type RecordingLimits } from '../../services/voiceRecorder';
import { outlineButton, primaryButton } from '../../components/styles';

const assertOwner = (ownerId: string) => {
    if (useAuthStore.getState().user?.id !== ownerId) throw new Error('The signed-in account changed; pending thoughts remain with their recording owner.');
  };

/** Up to ten minutes, and a full minute of quiet before it stops: room to walk the house and be reminded. */
const BRAIN_DUMP_LIMITS: RecordingLimits = {
  maxMs: BRAIN_DUMP_MAX_MS,
  silenceMs: BRAIN_DUMP_SILENCE_MS,
  limitNote: BRAIN_DUMP_LIMIT_NOTE,
  silenceNote: BRAIN_DUMP_SILENCE_NOTE,
};

/**
 * A brain dump (CER-225 phase 1; Easton, 2026-10-06: talk for five minutes and
 * get back an organized result). One tap starts it and one ends it. The words
 * are split into separate thoughts, and each is saved and filed as an
 * ordinary capture: anything Cerebro is unsure of waits in the Inbox.
 *
 * Phase 2 (CER-236): a thought that lacks its next step, the name of the
 * person it is about, or its when gets one short question first, for at
 * most five. Clear thoughts are saved at once; a questioned one is kept in
 * this browser and saved with its answer folded in, or as said when skipped.
 */
export default function BrainDumpPage() {
  const queryClient = useQueryClient();
  const [splitting, setSplitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [result, setResult] = useState<{ thoughts: string[]; waiting: number; noQuestions: boolean } | null>(null);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const questions = useQuestions(userId);
  const [answer, setAnswer] = useState('');
  const answerOwner = useRef(userId);
  const visibleAnswer = answerOwner.current === userId ? answer : '';
  const changeAnswer = (words: string) => { answerOwner.current = userId; setAnswer(words); };
  const resultOwner = useRef<string | null>(null);

  const questionState = useQuestionState();
  const settling = useRef(false);
  const [savingAnswer, setSavingAnswer] = useState(false);

  const resume = useCallback(async (ownerId: string) => {
    try {
      await processOwnerDump(ownerId, async () => {
      assertOwner(ownerId);
      await retryQuestionsStorage();
      await cleanupRecordingReceipts(ownerId);
      let noQuestions = false;
      for (const dump of await pendingDumps(ownerId)) {
        setSplitting(true);
        const split = await splitBrainDump(dump.transcript, ownerId);
        assertOwner(ownerId);
        const asked = new Map(split.questions.map(q => [q.index, q.ask]));
        await completeDump(ownerId, dump.id, split.items.map((thought, index) => ({ thought, ask: asked.get(index) })));
        noQuestions = split.questionsUnavailable;
      }
      const { thoughts, waiting } = await flushClearThoughts(ownerId);
      await cleanupRecordingReceipts(ownerId);
      assertOwner(ownerId);
      if (thoughts.length) { resultOwner.current = ownerId; setResult(current => ({ thoughts: [...(current?.thoughts ?? []), ...thoughts], waiting: (current?.waiting ?? 0) + waiting, noQuestions })); }
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.captures });
      });
    } catch (error) {
      setFailure(`Not saved yet: ${errorMessage(error)}. The pending copy is kept; retry to continue.`);
    } finally { setSplitting(false); }
  }, [queryClient]);
  useEffect(() => {
    setAnswer(''); setResult(null); setFailure(null);
    if (userId) void resume(userId);
  }, [userId, resume]);
  const retryStorage = async () => { if (userId) await resume(userId); };
  /** Freeze the answer before queue custody; retry uses its original words and capture identity. */
  const settleFirst = async (words: string) => {
    const first = questions[0]; const ownerId = userId;
    if (!first || !ownerId || settling.current) return;
    settling.current = true; setSavingAnswer(true);
    try {
      assertOwner(ownerId);
      const prepared = await prepareQuestion(ownerId, first.id, words);
      assertOwner(ownerId);
      const thought = prepared.captureText!;
      const { waiting } = await saveThoughts([{ text: thought, id: prepared.id, createdAt: prepared.createdAt }], ownerId);
      assertOwner(ownerId);
      await dropQuestion(ownerId, first.id);
      assertOwner(ownerId);
      setAnswer(''); setFailure(null); resultOwner.current = ownerId;
      setResult(current => ({ thoughts: [...(current?.thoughts ?? []), thought], waiting: (current?.waiting ?? 0) + waiting, noQuestions: current?.noQuestions ?? false }));
    } catch (error) { setFailure(`Not saved yet: ${errorMessage(error)}. Try again.`); }
    finally { settling.current = false; setSavingAnswer(false); }
  };

  const recorder = useVoiceRecorder(
    {
      recoveryKey: 'brain-dump',
      onCustodyReleased: (recordingId, ownerId) => retireRecordingReceipt(ownerId, recordingId),
      onStart: () => {
        setFailure(null);
        setNote(null);
      },
      onError: (reason) => setFailure(`Nothing was saved. ${reason}`),
      onLimit: setNote,
      onTranscript: async (words, ownerId, recordingId) => {
        await rememberDump(ownerId, words, recordingId);
        await resume(ownerId);
        return true;
      },
    },
    'tap_to_start',
    BRAIN_DUMP_LIMITS,
  );

  const recordingRecovery = recorder.recoveryCount > 0 ? <p role="status">{recorder.recoveryCount} recording(s) kept for this account. <button type="button" className={outlineButton} disabled={recorder.isTranscribing || splitting} onClick={() => void recorder.retry()}>Retry recording</button></p> : null;

  if (questionState.error) return <div role="alert" className="max-w-2xl mx-auto space-y-4"><p>{questionState.error}</p>{recordingRecovery}{failure && <p>{failure}</p>}<button type="button" className={primaryButton} onClick={() => void retryStorage()}>Retry storage</button></div>;

  if (questions.length > 0 && !splitting) {
    const first = questions[0];
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        {recordingRecovery}
        <p className="text-sm text-on-surface-variant">A quick question · {questions.length} to go</p>
        <blockquote className="rounded-xl bg-surface p-3 shadow-sm">{first.thought}</blockquote>
        <h1 className="text-xl font-semibold">{BRAIN_DUMP_QUESTION_TEXT[first.ask]}</h1>
        <DictationTextarea recordingKey={`brain-dump-answer:${first.id}`} key={first.id} label="Your answer" value={visibleAnswer} onChange={changeAnswer} disabled={savingAnswer || first.captureText !== undefined} rows={2} autoFocus />
        {first.captureText !== undefined && <p className="text-sm">Kept for retry: {first.captureText}</p>}
        {failure && <p className="text-sm text-error">{failure}</p>}
        <div className="flex gap-2 justify-end">
          <button type="button" className={outlineButton} disabled={savingAnswer || first.captureText !== undefined} onClick={() => void settleFirst('')}>
            Skip
          </button>
          <button type="button" className={primaryButton} disabled={savingAnswer || (!visibleAnswer.trim() && first.captureText === undefined)} onClick={() => void settleFirst(visibleAnswer)}>
            Save
          </button>
        </div>
      </div>
    );
  }

  if (result && resultOwner.current === userId) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        {recordingRecovery}
        <h1 className="text-2xl font-bold">
          Saved {result.thoughts.length} {result.thoughts.length === 1 ? 'thought' : 'thoughts'}
        </h1>
        <p className="text-sm text-on-surface-variant">
          Cerebro is filing each one now. Anything it is unsure of waits in your Inbox on Today.
          {result.waiting > 0 && ` ${result.waiting} are kept on this device and are sent when the connection is back.`}
        </p>
        {result.noQuestions && (
          <p className="text-sm text-on-surface-variant">Follow-up questions were not available this time, so every thought was saved as you said it.</p>
        )}
        <ul className="space-y-2">
          {result.thoughts.map((thought, index) => (
            <li key={`${index}:${thought}`} className="flex gap-2 items-start rounded-xl bg-surface p-3 shadow-sm">
              <CheckCircle2 className="w-4 h-4 mt-1 shrink-0 text-primary" />
              <span>{thought}</span>
            </li>
          ))}
        </ul>
        {failure && <><p className="text-sm text-error">{failure}</p><button type="button" className={outlineButton} onClick={() => void retryStorage()}>Retry pending thoughts</button></>}
        <div className="flex flex-wrap gap-2">
          <Link to="/" className={primaryButton}>
            See where they went
          </Link>
          <button type="button" className={outlineButton} onClick={() => setResult(null)}>
            Another brain dump
          </button>
        </div>
      </div>
    );
  }

  const busy = recorder.isTranscribing || splitting;
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">Brain dump</h1>
      {recordingRecovery}
      <p className="text-on-surface-variant">
        Say everything that is on your mind: things to do, to remember, to decide. Walk around if it helps; what you see will remind
        you. Up to 10 minutes, and a full minute of quiet ends it.
      </p>
      <div className="flex flex-col items-center gap-3 py-6">
        <button
          type="button"
          {...recorder.pressHandlers}
          disabled={busy}
          aria-label={recorder.isRecording ? 'Finish the brain dump' : 'Start a brain dump'}
          className={`w-32 h-32 rounded-full flex items-center justify-center ${recorder.isRecording ? 'bg-error text-on-error animate-pulse' : 'bg-primary-container text-primary'}`}
        >
          {busy ? <Loader2 className="w-12 h-12 animate-spin" /> : recorder.isRecording ? <Square className="w-12 h-12" /> : <Mic className="w-14 h-14" />}
        </button>
        <p role="status" className="font-medium">
          {recorder.isRecording
            ? 'Recording… tap to finish'
            : recorder.isTranscribing
              ? 'Listening back to it…'
              : splitting
                ? 'Sorting it into separate thoughts…'
                : 'Tap to start'}
        </p>
        {note && !recorder.isRecording && <p className="text-sm text-on-surface-variant">{note}</p>}
      </div>
      {failure && <><p className="text-sm text-error">{failure}</p><button type="button" className={outlineButton} onClick={() => void retryStorage()}>Retry pending thoughts</button></>}
    </div>
  );
}
